// @flow
import React from "react";
import { usePendingUploads } from "hooks";
import {
    type CardItemResultDTO,
    CardType,
    CardItemType,
    SearchItemType,
    type SidebarItemDTO,
} from "data/types";
import { type uppyFile } from "data/uppy";
import CardList from "components/CardList";
import { History } from "history";
import { withRouter } from "react-router";
import { isFileTypeMatch, isFileTagMatch } from "./uppyFileMatchUtils";
import queryString from "data/queryString";
import Constants from "data/constants";
import { globalSitesAreaSelector } from "data/storeHelper";
import "./index.css";
import { openPickerModal, PickerType } from "components/PickerModal";
import loc from "i18next";

function createLink(archiveTarget: CardItemResultDTO): string {
    switch (archiveTarget.uri) {
        case "at-fav":
        case "at-rec":
        case "at-search":
        case "at-picker":
            return archiveTarget.uri;
        // return `at-sf/${archiveTarget.searchItem.id}`;

        default:
            // pre-defined searchform?
            if (
                archiveTarget.searchItem &&
                archiveTarget.searchItem.id !== -1
            ) {
                if (archiveTarget.searchItem.searchType === SearchItemType.Form)
                    return "sf-custom";
                else return "sf";
            }
            return "uri"; //just open the card
    }
}

/**
 * will check if an archiveTargets should be visible, e.g. defined extras.fileTypes or extra.fileTags when it's supposed to be visible
 * by matching it with the first pending file upload, or hide with disabled Globalsites Area
 * @param {Array<CardItemResultDTO>} archiveTargets
 * @param {?Array<uppyFile>} pendingUploads
 * @param {?Array<SidebarItemDTO>} globalSites
 */
const filterArchiveTargets = (
    archiveTargets: Array<CardItemResultDTO>,
    pendingUploads: ?Array<uppyFile>,
    globalSites: ?Array<SidebarItemDTO>
) =>
    archiveTargets.filter((at) => {
        // check if GlobalSites Area is disabled
        if (at.uri === "at-picker" && globalSites == null) {
            console.info(
                `ArchiveTarget '${at.name}' skipped due to disabled GlobalSites Area (inPoint.Standalone > Admin > GeneralSettings > show/hide Globalsites)`
            );
            return false;
        }
        /* istanbul ignore if */
        if (pendingUploads == null || pendingUploads.length === 0) return true;
        /* istanbul ignore if */
        if (at.extras == null || at.extras.length === 0) return true;

        // check for any fileType match
        /* istanbul ignore else */
        if (at.extras.fileTypes && at.extras.fileTypes.length > 0) {
            let result = isFileTypeMatch(
                pendingUploads[0],
                at.extras.fileTypes
            );
            /* istanbul ignore else */
            if (!result) {
                console.info(
                    `ArchiveTarget '${at.name}' skipped due to .fileTypes requirement`,
                    at.extras.fileTypes
                );
                return false;
            }
        }

        // check all required file tags
        /* istanbul ignore else */
        if (at.extras.fileTags && at.extras.fileTags.length > 0) {
            let result = isFileTagMatch(pendingUploads[0], at.extras.fileTags);
            /* istanbul ignore else */
            if (!result) {
                console.info(
                    `ArchiveTarget '${at.name}' skipped due to .fileTags requirement`,
                    at.extras.fileTags
                );
                return false;
            }
        }
        return true;
    });

type Props = {
    /** list of archiveTargets to display */
    archiveTargets: Array<CardItemResultDTO>,
    /** React-Router withRouter History object */
    history: History,
};

/**
 * render the main archiveTargets as defined in appsettings.json
 */
export const ArchiveTargetList = ({ archiveTargets = [], history }: Props) => {
    const pendingUploads = usePendingUploads();
    const globalSites = globalSitesAreaSelector();

    const onCardClick = (archiveTarget: CardItemResultDTO, uri?: string) => {
        const qs = uri
            ? queryString.stringify({
                  mode: "uri-custom",
                  uri,
              })
            : queryString.stringify({
                  id: archiveTarget.id,
                  mode: createLink(archiveTarget),
              });
        history.push({
            search: "?" + qs,
        });
    };

    const showOnMoreClick = (card) =>
        card.type === CardType.Action && card.itemtype === CardItemType.Folder;

    const onMoreClick = async (card) => {
        const uri = await openPickerModal({
            pickerType: PickerType.Folder,
            scopeBreadcrumb: true,
            itemUri: card.uri,
            helpText: loc.t("upload:at-picker.description"),
        });
        if (uri === false) return false;
        onCardClick(card, uri);
    };

    return (
        <CardList
            status={Constants.OK}
            cards={filterArchiveTargets(
                archiveTargets,
                pendingUploads,
                globalSites
            )}
            filterText=""
            toggles={[CardType.Rec, CardType.Fav, CardType.Action]}
            showOnMoreClick={showOnMoreClick}
            onMoreClick={onMoreClick}
            onClick={(card) => onCardClick(card)}
        />
    );
};

ArchiveTargetList.displayName = "ArchiveTargetList";
export default withRouter(ArchiveTargetList);
